import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Heart, Brain, CheckCircle, Calendar, ArrowRight, AlertCircle, Zap } from 'lucide-react';
import CTA from '../components/CTA';

const JawTensionHypnotherapy: React.FC = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Can hypnotherapy stop teeth grinding?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, hypnotherapy works with the subconscious patterns that drive bruxism. Most clients notice reduced grinding within 3-4 sessions.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is jaw tension always stress-related?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Often, yes. The jaw is a primary tension-holding area. Hypnotherapy addresses the underlying stress response rather than just the symptom.",
        },
      },
      {
        '@type': 'Question',
        name: 'How many sessions for jaw tension hypnotherapy?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most clients see significant improvement in 3-6 sessions, depending on how long the pattern has been established.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can hypnotherapy help with TMJ pain?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Hypnotherapy can reduce the muscle tension contributing to TMJ pain and help retrain your jaw relaxation response.',
        },
      },
    ],
  };

  const symptoms = [
    { icon: <AlertCircle className="w-6 h-6 text-accent-600" />, text: 'Jaw clenching during the day, often without realising' },
    { icon: <AlertCircle className="w-6 h-6 text-accent-600" />, text: 'Teeth grinding at night (bruxism) — waking with a sore jaw' },
    { icon: <AlertCircle className="w-6 h-6 text-accent-600" />, text: 'TMJ pain, clicking, or restricted jaw movement' },
    { icon: <AlertCircle className="w-6 h-6 text-accent-600" />, text: 'Headaches and migraines triggered by jaw tension' },
    { icon: <AlertCircle className="w-6 h-6 text-accent-600" />, text: 'Stress-driven jaw tightening during work or difficult situations' },
  ];

  const benefits = [
    { icon: <Shield className="w-6 h-6 text-accent-600" />, title: 'Reduce the stress response driving bruxism' },
    { icon: <Zap className="w-6 h-6 text-accent-600" />, title: 'Release habitual jaw tension patterns' },
    { icon: <Heart className="w-6 h-6 text-accent-600" />, title: 'Improve sleep quality and reduce night grinding' },
    { icon: <Brain className="w-6 h-6 text-accent-600" />, title: "Retrain your jaw's relaxation response" },
  ];

  const faqs = [
    {
      question: 'Can hypnotherapy stop teeth grinding?',
      answer: 'Yes, hypnotherapy works with the subconscious patterns that drive bruxism. Most clients notice reduced grinding within 3-4 sessions.',
    },
    {
      question: 'Is jaw tension always stress-related?',
      answer: "Often, yes. The jaw is a primary tension-holding area. Hypnotherapy addresses the underlying stress response rather than just the symptom.",
    },
    {
      question: 'How many sessions for jaw tension hypnotherapy?',
      answer: 'Most clients see significant improvement in 3-6 sessions, depending on how long the pattern has been established.',
    },
    {
      question: 'Can hypnotherapy help with TMJ pain?',
      answer: 'Hypnotherapy can reduce the muscle tension contributing to TMJ pain and help retrain your jaw relaxation response.',
    },
  ];

  return (
    <main>
      <Helmet>
        <title>Jaw Tension &amp; Bruxism Hypnotherapy | Sharon Clare | Belfast &amp; NI</title>
        <meta
          name="description"
          content="Hypnotherapy for jaw tension, bruxism and teeth grinding in Belfast and Northern Ireland. Sharon Clare helps you release stress-driven jaw clenching and find lasting relief."
        />
        <meta
          name="keywords"
          content="jaw tension hypnotherapy, bruxism hypnotherapy Belfast, teeth grinding hypnotherapy, TMJ hypnotherapy Northern Ireland"
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/jaw-tension-hypnotherapy/" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
            Jaw Tension & Bruxism Hypnotherapy in Northern Ireland
          </h1>
          <p className="text-xl font-heading text-gray-700 mb-6">
            Release the stress-driven clenching and grinding that's causing you pain, disrupted sleep, and daily discomfort.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/book"
              className="btn-primary inline-flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" /> Request a 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            Do You Recognise These Symptoms?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {symptoms.map(({ icon, text }) => (
              <div key={text} className="bg-gray-50 rounded-xl p-5 border border-gray-100 flex items-start gap-3">
                <div className="shrink-0 mt-0.5">{icon}</div>
                <p className="text-gray-700">{text}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-700 leading-relaxed">
            Your jaw is one of the body's primary stress-holding areas. When your nervous system is on high alert, the jaw muscles tighten automatically — often without you being aware of it. Over time, this habitual tension can cause real pain and damage.
          </p>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            How Hypnotherapy Helps Jaw Tension & Bruxism
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Clinical hypnotherapy works directly with the subconscious stress response that triggers jaw clenching and teeth grinding. In a calm, focused state, your mind can learn to release the tension it's been holding — not just manage the symptoms, but address the root cause. Most clients find that once the underlying stress pattern is resolved, the jaw naturally begins to relax.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map(({ icon, title }) => (
              <div key={title} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center space-x-3">
                  {icon}
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">
            Common Questions About Jaw Tension Hypnotherapy
          </h2>
          <div className="space-y-6">
            {faqs.map(({ question, answer }) => (
              <div key={question} className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-2">{question}</h3>
                    <p className="text-gray-700 leading-relaxed">{answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar link */}
      <section className="py-8 bg-white">
        <div className="container-custom max-w-4xl text-center">
          <a href="/stress-physical-symptoms" className="text-accent-600 hover:text-accent-700 font-semibold inline-flex items-center gap-1">
            Learn more about stress-conditioned physical symptoms <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Ready to Release the Tension?"
        subtitle="Book your initial consultation and take the first step toward a relaxed, pain-free jaw."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default JawTensionHypnotherapy;
